import { useAuth0 } from "@auth0/auth0-react";
import { config } from "@config/config";
import { ExclamationTriangleIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { QueryClient } from "@tanstack/react-query";
import { ReactNode, useState } from "react";

import {
  AI_GENERATED,
  Button,
  Folder,
  FolderExplorer,
  GoalBankPanel,
  UserType,
} from "@fronterahealth/frontera-ui-components";

import {
  ApiReportReportTypeChoices,
  ReportTargetType,
  ReportTargetTypeEdge,
  TargetTimelineEstimationEnums,
  useAddSuggestedReportTargetsMutation,
  useCreateUpdateReportTargetMutation,
  useGoalBankTargetsQuery,
} from "@api/graphql/types-and-hooks";
import { AddTargetsBankCard } from "@components/GoalsBank/AddTargetsBank/AddTargetsBankCard";
import { GoalBankTargetsSaveToFolder } from "@components/GoalsBank/SavedGoals/Targets/GoalBankTargetsSaveToFolder";
import { DeleteFolderDialog } from "@components/GoalsBank/dialogs/DeleteFolderDialog";
import { useGoalBankFolders } from "@components/GoalsBank/hooks/useGoalBankFolders";
import {
  findFolder,
  getAiGeneratedFolderSection,
  getOrgFolderSection,
  getUserFolderSection,
} from "@components/GoalsBank/utils";
import { convertDBString } from "@components/forms/utils";
import { notifyError, notifySuccess } from "@components/notifications/notifications";

export interface AddTargetsBankProps {
  onAdd: () => void;
  assessmentId: string;
  reportGoalId: string;
  shortTermGoalBankId?: string;
  learnerId: string;
  targets: ReportTargetTypeEdge[];
  aiTargets: ReportTargetTypeEdge[];
  reportType?: ApiReportReportTypeChoices;
  isAiTargetsLoading?: boolean;
  isAiTargetsFailed?: boolean;
  hideAiTargets?: boolean;
  /** Optional custom trigger button. When provided, used instead of the default "Suggested Target" button. */
  modalButton?: ReactNode;
  className?: string;
}

export const AddTargetsBank: React.FC<AddTargetsBankProps> = ({
  targets,
  aiTargets,
  assessmentId,
  onAdd,
  learnerId,
  reportGoalId,
  shortTermGoalBankId,
  reportType,
  hideAiTargets = false,
  modalButton: modalButtonProp,
  isAiTargetsLoading,
  isAiTargetsFailed,
  className,
}) => {
  const [queryClient] = useState(() => new QueryClient());
  const { user } = useAuth0();
  const userType: UserType = !!user?.org_id ? "organization" : "selfServe";
  const orgName = `${user![config.auth0Audience + "/orgName"] ?? ""}`;
  const [selectedFolderId, setSelectedFolderId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { userFolders, orgFolders, createNewFolder, deleteFolder, editFolder } = useGoalBankFolders(searchTerm, false);
  const [deleteFolderId, setDeleteFolderId] = useState<string>("");
  const [openDeleteFolderDialog, setDeleteFolderDialogOpen] = useState(false);
  const [isSaveToFolderDialogOpen, setSaveToFolderDialogOpen] = useState<boolean>(false);
  const [selectedTargetIds, setSelectedTargetIds] = useState<string[]>([]);
  const [selectedAiTargetIds, setSelectedAiTargetIds] = useState<string[]>([]);
  const createUpdateTargetMutation = useCreateUpdateReportTargetMutation({});
  const addSuggestedTargets = useAddSuggestedReportTargetsMutation();
  const isReassessment = reportType === ApiReportReportTypeChoices.Reassessment;
  const goalBankTargets = useGoalBankTargetsQuery(
    { folderId: selectedFolderId, goalBankId: shortTermGoalBankId, searchTerm },
    {
      queryKey: ["targets-" + selectedFolderId + "search-term-" + searchTerm],
      refetchOnWindowFocus: false,
      refetchOnMount: "always",
      refetchInterval: 100000,
      enabled: !!selectedFolderId && selectedFolderId !== AI_GENERATED,
    },
  );

  const onSelectedFolder = (folder: Folder) => {
    setSelectedFolderId(folder.id);
  };

  const userFoldersSection = getUserFolderSection(userFolders);
  const orgFoldersSection = getOrgFolderSection(orgFolders, orgName);
  const aiSuggestedFoldersSection = getAiGeneratedFolderSection(aiTargets.length);

  const goalsToAdd = (): ReactNode[] => {
    if (selectedFolderId === AI_GENERATED) {
      if (isAiTargetsLoading) {
        return [
          <div key="ai-targets-loading-state" className="w-full">
            <div className="flex flex-col items-center justify-center p-8 text-center bg-white border border-limestone-200 rounded-lg">
              <div className="flex items-center gap-3 mb-6 animate-pulse">
                <SparklesIcon className="h-8 w-8 text-blurple-500" />
                <span className="text-limestone-700 font-medium">AI is generating targets...</span>
              </div>
              <div className="space-y-3 w-full max-w-md">
                <div className="animate-pulse">
                  <div className="bg-limestone-200 h-16 rounded-lg"></div>
                </div>
                <div className="animate-pulse">
                  <div className="bg-limestone-200 h-16 rounded-lg"></div>
                </div>
                <div className="animate-pulse">
                  <div className="bg-limestone-200 h-16 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>,
        ];
      }
      if (isAiTargetsFailed) {
        return [
          <div key="ai-targets-failed-state" className="w-full">
            <div className="flex flex-col items-center justify-center p-8 text-center bg-white border border-limestone-200 rounded-lg">
              <ExclamationTriangleIcon className="h-12 w-12 text-rio-600" />
              <h3 className="text-lg font-medium text-limestone-900 mb-2">AI Targets Generation Failed</h3>
              <p className="text-limestone-500">
                There was an error generating AI-suggested targets. You can still add targets from your saved folders or
                create custom targets.
              </p>
            </div>
          </div>,
        ];
      }
      return aiTargets.length
        ? aiTargets.map((edge) => {
            const node = edge?.node as ReportTargetType;
            return node ? (
              <AddTargetsBankCard
                id={node.id}
                targetName={node.targetName}
                targetDescription={node.masteryCriteria}
                selected={selectedAiTargetIds.includes(node.id)}
                onClick={() => {
                  if (selectedAiTargetIds.includes(node.id)) {
                    setSelectedAiTargetIds([...selectedAiTargetIds.filter((id) => id != node?.id)]);
                    return;
                  }
                  setSelectedAiTargetIds([...selectedAiTargetIds, node.id]);
                }}
              />
            ) : (
              <></>
            );
          })
        : [
            <div key="no-ai-targets" className="w-full">
              <div className="flex flex-col items-center justify-center p-8 text-center bg-white border border-limestone-200 rounded-lg">
                <p className="text-limestone-500">
                  No AI-generated targets available. Try adding targets from your saved folders or create custom
                  targets.
                </p>
              </div>
            </div>,
          ];
    }
    return (
      goalBankTargets.data?.getGoalBankTargets?.edges.map((edge) => {
        const node = edge?.node;
        return node ? (
          <AddTargetsBankCard
            id={node.id}
            targetName={node.targetName}
            targetDescription={node.targetDescription}
            selected={selectedTargetIds.includes(node.id)}
            onClick={() => {
              if (selectedTargetIds.includes(node.id)) {
                setSelectedTargetIds([...selectedTargetIds.filter((id) => id != node?.id)]);
                return;
              }
              setSelectedTargetIds([...selectedTargetIds, node.id]);
            }}
          />
        ) : (
          <></>
        );
      }) || []
    );
  };

  const addGoals = async () => {
    if (selectedTargetIds.length === 0 && selectedAiTargetIds.length === 0) return;

    try {
      const isAiGeneratedFolder = selectedFolderId === AI_GENERATED;
      if (!isAiGeneratedFolder) {
        const targetsToAdd = goalBankTargets.data?.getGoalBankTargets?.edges.filter((edge) =>
          selectedTargetIds.includes(edge?.node?.id ?? "<missing-target-id>"),
        );
        targetsToAdd?.forEach(async (target) => {
          const node = target?.node;
          await createUpdateTargetMutation.mutateAsync(
            {
              targetData: {
                targetName: node?.targetName ?? "",
                reportGoalId: reportGoalId,
                targetDescription: node?.targetDescription,
                masteryCriteria: node?.masteryCriteria,
                timelineEstimationType: node?.timelineEstimationType
                  ? (convertDBString(node?.timelineEstimationType) as TargetTimelineEstimationEnums)
                  : null,
                timelineEstimationValue: node?.timelineEstimationValue,
                reportId: assessmentId,
              },
              learnerId: learnerId ? learnerId : "<missing-learner-id>",
            },
            {
              onSuccess: async () => {
                await onAdd();
              },
            },
          );
        });
      }
      if (isAiGeneratedFolder) {
        await addSuggestedTargets.mutateAsync(
          {
            aiSuggestedTargetsIds: selectedAiTargetIds,
            reportId: assessmentId,
            reportGoalId,
          },
          {
            onSuccess: async () => {
              await onAdd();
            },
          },
        );
      }
    } catch (err) {
      notifyError("Failed To Add Target(s)");
      console.error("Error adding AI goals", err);
    } finally {
      setSelectedTargetIds([]);
      notifySuccess("Successfully Added Target(s)");
    }
  };

  const showDeleteFolderDialog = (folderId: string) => {
    setDeleteFolderId(folderId);
    setDeleteFolderDialogOpen(true);
  };

  return (
    <>
      <FolderExplorer
        userType={userType}
        selectedFolderId={selectedFolderId}
        onDeleteFolder={(folderId) => {
          showDeleteFolderDialog(folderId);
        }}
        onEditFolder={(id, newValue) => editFolder(findFolder(id, userFolders.concat(orgFolders)), newValue)}
        onCreateFolder={async (folderName, folderType) => {
          const newFolder = await createNewFolder(folderName, folderType);
          setSelectedFolderId(newFolder.id);
        }}
        onSelectedFolder={onSelectedFolder}
        onSearch={(value) => {
          setSearchTerm(value);
        }}
        showSearch={true}
        title={"Add Targets"}
        folderSections={
          orgName
            ? [aiSuggestedFoldersSection, userFoldersSection, orgFoldersSection]
            : [aiSuggestedFoldersSection, userFoldersSection]
        }
        modalButton={
          modalButtonProp ??
          (isReassessment ? (
            <Button
              data-testid="suggested-target-button"
              text={
                <div className="flex items-center">
                  {!hideAiTargets && <SparklesIcon className="w-4 h-4 mr-1" />}
                  <span>{hideAiTargets ? "+ Goal Bank Target" : "Suggested Target"}</span>
                </div>
              }
              appearance={"secondary"}
              className={`text-green-700 hover:bg-white [&]:border-green-700! [&:hover]:border-green-700! focus:border-green-700! active:border-green-700! ${className ?? ""}`}
              type="button"
              style={{ fontWeight: 400 }}
            />
          ) : (
            <Button
              data-testid="suggested-target-button"
              variant="secondary"
              buttonStyle="filled"
              className={className}
              text={
                <div className="flex items-center">
                  {!hideAiTargets && <SparklesIcon className="w-4 h-4 mr-1" />}
                  <span>{hideAiTargets ? "+ Goal Bank Target" : "Suggested Target"}</span>
                </div>
              }
            />
          ))
        }
        footer={{
          addButtonText: "Add Target(s)",
          onAdd: addGoals,
          onSaveToFolder: () => setSaveToFolderDialogOpen(true),
          displaySaveToFolderButton: selectedFolderId === AI_GENERATED,
        }}
        panel={
          <GoalBankPanel
            userType={userType}
            isLoading={goalBankTargets.isFetching || goalBankTargets.isLoading}
            key={goalsToAdd().length}
            noFolders={false}
            goalsToDisplay={goalBankTargets.isFetching || goalBankTargets.isLoading ? [] : goalsToAdd()}
            goalsTabs={[]}
            onCreateFolder={() => {}}
            noGoalsEmptyState={{
              header: selectedFolderId == AI_GENERATED ? "No AI-Generated Items Available" : "Nothing to Show",
              subHeader:
                selectedFolderId == AI_GENERATED
                  ? "You may add saved items from the folders on the left, or add custom items on the previous screen."
                  : "Save items to this folder for later use.",
            }}
          ></GoalBankPanel>
        }
      >
        <GoalBankTargetsSaveToFolder
          key={selectedAiTargetIds.length + selectedTargetIds.length}
          open={isSaveToFolderDialogOpen}
          aiTargets={
            aiTargets
              .filter((aiTarget) => !!aiTarget && selectedAiTargetIds.includes(aiTarget.node?.id ?? ""))
              .map((edge) => edge?.node) as ReportTargetType[]
          }
          targets={
            targets
              .filter((target) => !!target && selectedTargetIds.includes(target.node?.id ?? ""))
              .map((edge) => edge.node) as ReportTargetType[]
          }
          refetch={async () => await queryClient.refetchQueries()}
          onCloseClick={() => {
            setSaveToFolderDialogOpen(false);
          }}
        />
        <DeleteFolderDialog
          open={openDeleteFolderDialog}
          folderName={findFolder(deleteFolderId, userFolders.concat(orgFolders)).name}
          onCancelClick={() => {
            setDeleteFolderId("");
            setDeleteFolderDialogOpen(false);
          }}
          onDeleteClick={() => {
            if (deleteFolderId == selectedFolderId) {
              setSelectedFolderId("");
            }
            deleteFolder(findFolder(deleteFolderId, userFolders.concat(orgFolders)));
            setDeleteFolderId("");
            setDeleteFolderDialogOpen(false);
          }}
        />
      </FolderExplorer>
    </>
  );
};
